import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useSnapshot } from 'valtio';
import {
  useBrandRulesQuery,
  useUpdateBrandRulesMutation,
} from '../../services/brandRulesService/__generated__/queries.generated';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Field,
  FormGrid,
  LoadingBlock,
  Select,
} from '../../components/ui';
import { toastActions } from '../../../store/toastStore';
import { authStore } from '../../../store/authStore';
import {
  COUNTRY_LABELS,
  Country,
  CURRENCY_LABELS,
  CurrencyCode,
  currencyLockedMessage,
  MEASUREMENT_LABELS,
  MeasurementSystem,
  regionConfirm,
  regionFormOf,
  RegionForm,
  regionPatch,
  regionSuggestion,
} from './regionAdmin';

function isStale(error: unknown): boolean {
  const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: { code?: string } }> })
    ?.graphQLErrors;
  return Boolean(graphQLErrors?.some(e => e.extensions?.code === 'BRAND_RULES_STALE'));
}

/**
 * Spec 028: the brand's country, currency and units. Only the platform owner
 * changes them; brand admins see the values. The server converts the brand's
 * own distance rules on a unit change and refuses a new currency once the
 * brand has trips.
 */
export function RegionSection({ brandName }: { brandName: string }) {
  const { user } = useSnapshot(authStore);
  const isPlatformOwner = Boolean((user as { isPlatformOwner?: boolean } | null)?.isPlatformOwner);
  const { data, loading, error, refetch } = useBrandRulesQuery({ fetchPolicy: 'network-only' });
  const [updateBrandRules, { loading: saving }] = useUpdateBrandRulesMutation({
    refetchQueries: ['BrandRulesHistory', 'BrandLocale'],
  });

  const rules = data?.brandRules?.bookingRules as Record<string, unknown> | undefined;
  const loaded = useMemo(() => regionFormOf(rules), [rules]);
  const [form, setForm] = useState<RegionForm>(loaded);
  const [fieldError, setFieldError] = useState<string | null>(null);
  useEffect(() => setForm(loaded), [loaded]);

  const patch = regionPatch(loaded, form);

  const save = async () => {
    if (!data?.brandRules || !patch) return;
    setFieldError(null);
    try {
      await updateBrandRules({
        variables: { input: { version: data.brandRules.version, bookingRules: patch } },
      });
      toast.success(`Region saved for ${brandName}. Every server follows within about a minute.`);
      await refetch();
    } catch (e) {
      if (isStale(e)) {
        toast.error('Someone else changed these rules. The latest values are loaded; try again.');
        await refetch();
        return;
      }
      const locked = currencyLockedMessage(e);
      if (locked) {
        setFieldError(locked);
        return;
      }
      toast.error((e as Error).message || 'Could not save');
    }
  };

  const ask = () => {
    const confirm = regionConfirm(brandName, loaded, form, rules);
    toastActions.showToastAlert(
      confirm.title,
      confirm.body,
      '',
      () => void save(),
      () => undefined,
      confirm.confirm,
      'Cancel',
    );
  };

  const readOnly = !isPlatformOwner || saving;

  return (
    <Card>
      <CardHeader
        title="Region"
        description={`Where ${brandName} operates: the country for addresses and holidays, the currency of every price and charge, and the units for distances and areas.`}
      />
      {loading && <LoadingBlock label="Loading region" />}
      {error && (
        <CardBody>
          <Alert tone="error" title="Could not load the region">
            {error.message}
          </Alert>
        </CardBody>
      )}
      {!loading && !error && (
        <>
          <CardBody>
            <FormGrid $cols={2}>
              <Field label="Country" htmlFor="region-country">
                <Select
                  id="region-country"
                  data-testid="region-country"
                  value={form.country}
                  disabled={readOnly}
                  onChange={e => setForm(regionSuggestion(e.target.value as Country))}
                >
                  {(Object.keys(COUNTRY_LABELS) as Country[]).map(code => (
                    <option key={code} value={code}>
                      {COUNTRY_LABELS[code]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Currency" htmlFor="region-currency" error={fieldError ?? undefined}>
                <Select
                  id="region-currency"
                  data-testid="region-currency"
                  value={form.currency}
                  disabled={readOnly}
                  onChange={e => setForm({ ...form, currency: e.target.value as CurrencyCode })}
                >
                  {(Object.keys(CURRENCY_LABELS) as CurrencyCode[]).map(code => (
                    <option key={code} value={code}>
                      {CURRENCY_LABELS[code]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Measurement units"
                htmlFor="region-units"
                hint={
                  isPlatformOwner
                    ? "Changing units converts this brand's own distance rules. Trips already booked keep their units."
                    : 'Set by the platform owner.'
                }
              >
                <Select
                  id="region-units"
                  data-testid="region-units"
                  value={form.measurementSystem}
                  disabled={readOnly}
                  onChange={e =>
                    setForm({ ...form, measurementSystem: e.target.value as MeasurementSystem })
                  }
                >
                  {(Object.keys(MEASUREMENT_LABELS) as MeasurementSystem[]).map(code => (
                    <option key={code} value={code}>
                      {MEASUREMENT_LABELS[code]}
                    </option>
                  ))}
                </Select>
              </Field>
            </FormGrid>
          </CardBody>
          {isPlatformOwner && (
            <CardFooter>
              <Button
                theme="filled"
                text="Save region"
                onClick={ask}
                isDisabled={!patch || saving}
                isLoading={saving}
              />
            </CardFooter>
          )}
        </>
      )}
    </Card>
  );
}

export default RegionSection;
